/**
 * The caller's own sidebar pins (backend routers/sidebar.py): the entities
 * and connections they chose to keep at hand, drawn by NavBar under the
 * built-in pages and toggled by the pin button on an entity's or a
 * connection's page (components/Sidebar/PinToSidebarButton.jsx).
 *
 * One list for the whole app: every route mounts its own Layout and NavBar,
 * so the pins live in a module-level store that each hook instance
 * subscribes to — fetched once per signed-in user, updated in place by pin()
 * and unpin(), and in step everywhere the moment either lands.
 *
 *   const { pins, findEntityPin, pinEntity, unpin } = useSidebarPins();
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

// { pins: null until the first fetch settles (then an array), userId }
let store = { pins: null, userId: null };
const listeners = new Set();
let inFlight = null;

function set(patch) {
    store = { ...store, ...patch };
    listeners.forEach(fn => fn(store));
}

function load(api, userId, { force = false } = {}) {
    if (!force && store.userId === userId && store.pins !== null) return Promise.resolve(store.pins);
    if (!force && inFlight) return inFlight;
    if (store.userId !== userId) set({ pins: null, userId });
    inFlight = api.get('/api/sidebar/pins')
        .then(pins => { set({ pins: Array.isArray(pins) ? pins : [] }); return store.pins; })
        // A node that can't answer (older backend, offline) just has no
        // pins; the buttons stay usable and the next pin retries the list.
        .catch(() => { set({ pins: store.pins ?? [] }); return store.pins; })
        .finally(() => { inFlight = null; });
    return inFlight;
}

// Tests and logout: forget what was loaded.
export function resetSidebarPins() {
    inFlight = null;
    set({ pins: null, userId: null });
}

export function useSidebarPins() {
    const api = useApi();
    const { isAuthenticated, user } = useAuth();
    const userId = user?.id ?? null;
    const [state, setState] = useState(store);

    useEffect(() => {
        listeners.add(setState);
        setState(store);
        return () => { listeners.delete(setState); };
    }, []);

    useEffect(() => {
        if (isAuthenticated && userId) load(api, userId);
    }, [api, isAuthenticated, userId]);

    const pins = useMemo(() => state.pins ?? [], [state.pins]);
    const findEntityPin = useCallback(
        id => pins.find(p => p.entity && p.entity.id === Number(id)) ?? null,
        [pins],
    );
    const findConnectionPin = useCallback(
        id => pins.find(p => p.connection && p.connection.id === Number(id)) ?? null,
        [pins],
    );
    const pin = useCallback(async body => {
        const created = await api.post('/api/sidebar/pins', body);
        // The backend hands back the existing pin when it was already there.
        set({ pins: [...(store.pins ?? []).filter(p => p.id !== created.id), created] });
        return created;
    }, [api]);
    const pinEntity = useCallback(id => pin({ entity_id: Number(id) }), [pin]);
    const pinConnection = useCallback(id => pin({ connection_id: Number(id) }), [pin]);
    const unpin = useCallback(async pinId => {
        await api.del(`/api/sidebar/pins/${pinId}`);
        set({ pins: (store.pins ?? []).filter(p => p.id !== pinId) });
    }, [api]);
    const refresh = useCallback(() => (userId ? load(api, userId, { force: true }) : Promise.resolve([])), [api, userId]);

    return {
        pins,
        // False until the list has been fetched once (or that fetch failed).
        ready: state.pins !== null,
        findEntityPin,
        findConnectionPin,
        pinEntity,
        pinConnection,
        unpin,
        refresh,
    };
}

export default useSidebarPins;
